import { h, Fragment } from 'preact';
import Player from '@vimeo/player';
import { useEffect, useState, useId, useRef } from 'preact/hooks';
import Icon from 'utils/icon';

/**
 *
 * @param {externalId} Number Vimeo video id
 * @param {autoplay} Boolean
 * @param {muted} Boolean
 * @param {loop} Boolean
 * @param {className} String
 * @param {onPlayerInit} Function
 * @param {showControls} Boolean
 * @param {isHTML5} Boolean
 * @param {videoSrc} String - HTML5 video src
 * @returns ReactVideo component
 */
const ReactVideo = ({
  externalId,
  autoplay,
  muted,
  id,
  loop = true,
  className,
  onPlayerInit,
  showControls = true,
  isHTML5 = false,
  videoSrc = '',
  onLoad,
}) => {
  const videoId = useId();

  const [isPlaying, setIsPlaying] = useState(autoplay);
  const [player, setPlayer] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (onLoad) setTimeout(() => onLoad(), 1000);
    if (!externalId) return;

    ((player && player.destroy()) || Promise.resolve()).then(() => {
      const playerInst = new Player(videoId, {
        id: externalId,
        autoplay,
        muted: autoplay || muted ? true : false,
        loop,
        controls: false,
        playsinline: true,
        autopause: false,
        showControls,
      });
      setPlayer(playerInst);
      onPlayerInit(playerInst);
    });
  }, [id]);

  const handlePlay = () => {
    if (isPlaying) {
      isHTML5 ? videoRef.current.pause() : player.pause();
      setIsPlaying(false);
    } else {
      isHTML5 ? videoRef.current.play() : player.play();
      setIsPlaying(true);
    }
  };

  return (
    <div className={`react-video-container relative ${className}`}>
      {isHTML5 && (
        <video
          id={videoId}
          src={videoSrc}
          className="object-cover w-full h-full"
          autoPlay={autoplay}
          muted={autoplay || muted ? true : false}
          loop={loop}
          playsInline
          ref={videoRef}
          controls={false}
          onLoad={onLoad}
        ></video>
      )}
      {!isHTML5 && (
        <div
          id={videoId}
          className="h-full w-full pointer-events-none"
          onLoad={onLoad}
        ></div>
      )}
      {showControls && (
        <div
          data-video-play
          onClick={handlePlay}
          className="cursor-pointer"
          ariaLabel={isPlaying ? 'Pause video' : 'Play video'}
        >
          <Icon
            id="play"
            className={`absolute w-10 h-10 right-[40px] bottom-[40px] z-1 ${
              !!isPlaying ? 'hidden' : ''
            }`}
          />
          <Icon
            id="pause"
            className={`absolute w-10 h-10 right-[40px] bottom-[40px] z-1 ${
              !isPlaying ? 'hidden' : ''
            }`}
          />
        </div>
      )}
    </div>
  );
};

export default ReactVideo;
